(() => {
  if (window.vh12MotionController) { window.vh12MotionController.refresh(); return; }
  const root = document.documentElement;
  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  let paused = preference.matches;
  let optedIn = false;
  let styleReady = false;
  let observer;
  let raf = 0;
  let frames = [];
  let active = new Set();
  let hero;
  let letters = [];
  let heroVisible = true;
  let loopActive = false;
  let phase = 'reveal';
  let letterIndex = 0;
  let timer = null;
  let dueAt = 0;
  let remaining = 100;
  let filmFrame = null;
  let filmTime = null;
  const enabled = () => !preference.matches || optedIn;
  const canTick = () => styleReady && enabled() && !paused && !document.hidden && heroVisible && loopActive && !filmFrame;
  const pauseTimer = () => {
    if (timer !== null) {
      remaining = Math.max(0, dueAt - performance.now());
      clearTimeout(timer);
      timer = null;
    }
  };
  const schedule = () => {
    if (timer !== null || !canTick()) return;
    dueAt = performance.now() + remaining;
    timer = setTimeout(tick, remaining);
  };
  const tick = () => {
    timer = null;
    if (!canTick()) return;
    if (phase === 'reveal') {
      if (letters[letterIndex]) letters[letterIndex].classList.add('vh12-letter-visible');
      letterIndex += 1;
      if (letterIndex >= letters.length) { phase = 'hold'; remaining = 2200; }
      else remaining = 75;
    } else if (phase === 'hold') {
      letters.forEach(letter => letter.classList.remove('vh12-letter-visible'));
      phase = 'reset';
      remaining = 300;
    } else {
      letterIndex = 0;
      phase = 'reveal';
      remaining = 0;
    }
    schedule();
  };
  const stopLoop = () => {
    pauseTimer();
    loopActive = false;
    if (hero) hero.classList.remove('vh12-loop-active');
    letters.forEach(letter => letter.classList.remove('vh12-letter-visible'));
  };
  const startLoop = () => {
    if (!hero || !letters.length || loopActive) return;
    letterIndex = 0;
    phase = 'reveal';
    remaining = 100;
    letters.forEach(letter => letter.classList.remove('vh12-letter-visible'));
    hero.classList.add('vh12-loop-active');
    loopActive = true;
  };
  // A playing hero film supplies one clock for both visual layers.
  // Null time keeps the complete heading readable while media is loading or unavailable.
  const renderFilmLetters = () => {
    if (!filmFrame) return;
    const count = filmTime === null ? letters.length : Math.max(0, Math.floor((filmTime * 1000 - 100) / 75) + 1);
    letters.forEach((letter, index) => letter.classList.toggle('vh12-letter-visible', index < count));
  };
  const setHeroFilmTime = (frame, time) => {
    if (!hero || !frame.isConnected || !hero.contains(frame)) return false;
    filmFrame = frame;
    filmTime = Number.isFinite(time) ? Math.max(0, time) : null;
    pauseTimer();
    if (styleReady && enabled()) startLoop();
    renderFilmLetters();
    return true;
  };
  const releaseHeroFilmClock = frame => {
    if (filmFrame !== frame) return;
    filmFrame = null;
    filmTime = null;
    stopLoop();
    sync();
  };
  const clearOffsets = () => frames.forEach(frame => {
    frame.style.removeProperty('--vh12-x');
    frame.style.removeProperty('--vh12-y');
  });
  const scrollFrame = () => {
    raf = 0;
    if (!styleReady || paused || !enabled() || document.hidden) return;
    active.forEach(frame => {
      const box = frame.getBoundingClientRect();
      const progress = Math.max(-1, Math.min(1, ((window.innerHeight / 2) - (box.top + box.height / 2)) / (window.innerHeight / 2 + box.height / 2)));
      const padding = parseFloat(getComputedStyle(frame).paddingTop) || 0;
      const amplitude = Math.min(12, Math.max(0, padding - 3));
      const direction = Number(frame.dataset.vh12Direction || 1);
      frame.style.setProperty('--vh12-y', `${(progress * amplitude * direction).toFixed(2)}px`);
      frame.style.setProperty('--vh12-x', `${(progress * Math.min(3, amplitude / 3) * -direction).toFixed(2)}px`);
    });
  };
  const requestFrame = () => { if (!raf && styleReady && !paused && enabled() && !document.hidden) raf = requestAnimationFrame(scrollFrame); };
  const sync = () => {
    root.classList.toggle('vh12-motion-ready', styleReady && enabled());
    root.classList.toggle('vh12-motion-opt-in', styleReady && optedIn);
    root.classList.toggle('vh12-motion-paused', paused || document.hidden || !styleReady);
    if (styleReady && enabled()) { startLoop(); renderFilmLetters(); if (canTick()) schedule(); else pauseTimer(); }
    else stopLoop();
    document.querySelectorAll('[data-vh12-motion-toggle]').forEach(button => {
      button.disabled = !styleReady;
      button.textContent = !paused && enabled() ? 'Pause animations' : 'Play animation';
    });
    document.querySelectorAll('[data-vh12-replay]').forEach(button => { button.disabled = !styleReady; });
    document.querySelectorAll('[data-vh12-motion-status]').forEach(status => {
      status.textContent = !styleReady ? 'Animation styles are loading.' : preference.matches && !optedIn ? 'Reduced motion is on. Play is optional.' : document.hidden || paused ? 'Animations paused.' : 'Headline reveal repeats. Pause anytime.';
    });
    requestFrame();
  };
  const replay = () => {
    if (!styleReady) return;
    stopLoop();
    paused = false;
    optedIn = preference.matches;
    sync();
  };
  const initialize = () => {
    try {
      const nextHero = document.querySelector('.vh12-hero');
      if (!nextHero) return;
      if (hero !== nextHero) { filmFrame = null; filmTime = null; stopLoop(); hero = nextHero; letters = Array.from(hero.querySelectorAll('.vh12-letter')); }
      styleReady = getComputedStyle(hero).getPropertyValue('--vh12-style-ready').trim() === '1';
      frames = Array.from(document.querySelectorAll('.vh12-motion-frame'));
      active.clear();
      if (observer) observer.disconnect();
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.target === hero) {
              heroVisible = entry.isIntersecting;
              if (canTick()) schedule(); else pauseTimer();
            } else if (entry.target.classList.contains('vh12-motion-frame')) {
              entry.target.classList.toggle('vh12-in-view', entry.isIntersecting);
              if (entry.isIntersecting) active.add(entry.target); else active.delete(entry.target);
            } else if (entry.isIntersecting && enabled() && !paused) entry.target.classList.add('vh12-entered');
          });
          requestFrame();
        }, { threshold: 0.12 });
        observer.observe(hero);
        frames.forEach((frame, i) => { frame.dataset.vh12Direction = i % 2 ? '-1' : '1'; observer.observe(frame); });
        document.querySelectorAll('.vh12-wrap:not(.vh12-hero)').forEach(section => observer.observe(section));
      } else {
        heroVisible = true;
        frames.forEach((frame, i) => { frame.dataset.vh12Direction = i % 2 ? '-1' : '1'; frame.classList.add('vh12-in-view'); active.add(frame); });
      }
      sync();
    } catch (error) {
      stopLoop();
      styleReady = false;
      root.classList.remove('vh12-motion-ready', 'vh12-motion-opt-in');
      root.classList.add('vh12-motion-paused');
      document.querySelectorAll('[data-vh12-motion-toggle], [data-vh12-replay]').forEach(button => { button.disabled = true; });
      document.querySelectorAll('[data-vh12-motion-status]').forEach(status => { status.textContent = 'Animation could not start. Reload to try again.'; });
      console.warn('VANDA animation initialization did not complete.', error);
    }
  };
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-vh12-replay]')) replay();
    if (event.target.closest('[data-vh12-motion-toggle]') && styleReady) {
      if (!paused && enabled()) paused = true;
      else { paused = false; optedIn = preference.matches; }
      sync();
    }
  });
  const preferenceChanged = () => {
    optedIn = false;
    paused = preference.matches;
    clearOffsets();
    sync();
  };
  if (preference.addEventListener) preference.addEventListener('change', preferenceChanged);
  else if (preference.addListener) preference.addListener(preferenceChanged);
  window.addEventListener('scroll', requestFrame, { passive: true });
  window.addEventListener('resize', requestFrame, { passive: true });
  document.addEventListener('visibilitychange', sync);
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('load', event => { if (event.target instanceof HTMLLinkElement && event.target.href.includes('vh12-home.css')) initialize(); }, true);
  window.addEventListener('load', initialize, { once: true });
  window.vh12MotionController = { refresh: initialize, setHeroFilmTime, releaseHeroFilmClock };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
(() => {
  if (window.vh12QuoteController) {
    window.vh12QuoteController.refresh();
    return;
  }
  const root = document.documentElement;
  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const visible = new Set();
  let observer;

  const settle = element => {
    element.dataset.vh12QuoteDone = 'true';
    element.classList.remove('vh12-quote-revealing');
  };

  const reveal = () => {
    if (preference.matches) {
      document.querySelectorAll('.vh12-quote-revealing').forEach(settle);
      return;
    }
    if (!root.classList.contains('vh12-motion-ready') ||
        root.classList.contains('vh12-motion-paused') ||
        document.hidden) return;

    visible.forEach(element => {
      if (element.dataset.vh12QuoteDone || element.dataset.vh12QuoteStarted) return;
      if (getComputedStyle(element).getPropertyValue('--vh12-quote-motion-ready').trim() !== '1') return;
      element.dataset.vh12QuoteStarted = 'true';
      element.classList.add('vh12-quote-revealing');
    });
  };

  const initialize = () => {
    if (!('IntersectionObserver' in window)) return;
    if (observer) observer.disconnect();
    visible.clear();
    observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio >= .15) {
          visible.add(entry.target);
        } else {
          visible.delete(entry.target);
        }
      });
      reveal();
    }, { threshold: .15 });
    document.querySelectorAll('.vh12 .vh12-quote').forEach(element => observer.observe(element));
  };

  document.addEventListener('animationend', event => {
    if (event.animationName === 'vh12-quote-arrive') settle(event.target);
  });
  new MutationObserver(reveal).observe(root, {
    attributes: true,
    attributeFilter: ['class']
  });
  document.addEventListener('visibilitychange', reveal);
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', event => {
    if (!(event.target instanceof Element)) return;
    event.target.querySelectorAll('.vh12-quote').forEach(element => {
      visible.delete(element);
      if (observer) observer.unobserve(element);
    });
  });
  if (preference.addEventListener) {
    preference.addEventListener('change', reveal);
  } else if (preference.addListener) {
    preference.addListener(reveal);
  }
  window.addEventListener('load', initialize, { once: true });
  window.vh12QuoteController = { refresh: initialize };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
